import { useState, type FormEvent } from "react";
import { postJson } from "../lib/api";
import { TilePicker, TileMultiPicker, type TileOption } from "./FormTiles";

export type WaitlistProgram = "elder" | "orphan";

interface Props {
  program: WaitlistProgram;
}

type Relation =
  | "self"
  | "family"
  | "neighbor"
  | "social_worker"
  | "ngo"
  | "cwc"
  | "other";

type Urgency = "immediate" | "weeks" | "months" | "planning";
type Gender = "f" | "m" | "other" | "prefer_not";
type Mobility = "independent" | "assisted" | "wheelchair" | "bedridden";
type LegalStatus = "cwc_order" | "in_process" | "informal" | "unsure";
type SchoolingStatus = "enrolled" | "gap" | "never" | "dropped";

interface FormState {
  // applicant
  applicantName: string;
  applicantAge: string;
  applicantGender: Gender | "";
  applicantMobility: Mobility | ""; // elder-only
  medicalConditions: string[]; // multi
  medicalOther: string;
  dietaryNeeds: string[]; // multi
  currentLocation: string; // where they are now
  currentLivingSituation: string; // street/relatives/hospital/etc
  documentsAvailable: string[]; // multi (aadhaar, ration, etc)
  // orphan-only
  schoolingStatus: SchoolingStatus | "";
  siblingsCount: string;
  legalStatus: LegalStatus | "";
  // submitter
  submitterName: string;
  submitterRelation: Relation | "";
  submitterRelationOther: string;
  orgName: string; // for social_worker / ngo / cwc
  phone: string;
  email: string;
  city: string;
  state: string;
  urgency: Urgency | "";
  situation: string;
  consent: boolean;
}

const INITIAL: FormState = {
  applicantName: "",
  applicantAge: "",
  applicantGender: "",
  applicantMobility: "",
  medicalConditions: [],
  medicalOther: "",
  dietaryNeeds: [],
  currentLocation: "",
  currentLivingSituation: "",
  documentsAvailable: [],
  schoolingStatus: "",
  siblingsCount: "",
  legalStatus: "",
  submitterName: "",
  submitterRelation: "",
  submitterRelationOther: "",
  orgName: "",
  phone: "",
  email: "",
  city: "",
  state: "Tamil Nadu",
  urgency: "",
  situation: "",
  consent: false,
};

// ---- options ----

const RELATION_OPTIONS_ELDER: TileOption<Relation>[] = [
  { value: "self", label: "Myself", hint: "I am the elder seeking sanctuary" },
  { value: "family", label: "Family member", hint: "Parent, grandparent, sibling, etc." },
  { value: "neighbor", label: "Neighbour / friend", hint: "Someone in my community" },
  { value: "social_worker", label: "Social worker", hint: "Professional capacity" },
  { value: "ngo", label: "NGO / institution", hint: "On behalf of an organization" },
  { value: "other", label: "Other", hint: "Please describe" },
];
const RELATION_OPTIONS_ORPHAN: TileOption<Relation>[] = [
  { value: "family", label: "Relative", hint: "Aunt, uncle, grandparent, sibling, etc." },
  { value: "neighbor", label: "Neighbour / friend", hint: "Someone in the community" },
  { value: "social_worker", label: "Social worker", hint: "Professional capacity" },
  { value: "ngo", label: "NGO", hint: "On behalf of an organization" },
  { value: "cwc", label: "CWC / govt", hint: "Child Welfare Committee referral" },
  { value: "other", label: "Other", hint: "Please describe" },
];

const URGENCY: TileOption<Urgency>[] = [
  { value: "immediate", label: "Immediate", hint: "Within days" },
  { value: "weeks", label: "Soon", hint: "Within weeks" },
  { value: "months", label: "Planning", hint: "Within months" },
  { value: "planning", label: "Future", hint: "Preparing ahead" },
];

const GENDER: TileOption<Gender>[] = [
  { value: "f", label: "Female" },
  { value: "m", label: "Male" },
  { value: "other", label: "Other" },
  { value: "prefer_not", label: "Prefer not to say" },
];

const MOBILITY: TileOption<Mobility>[] = [
  { value: "independent", label: "Independent", hint: "Walks unaided" },
  { value: "assisted", label: "Assisted", hint: "Cane / walker / partial help" },
  { value: "wheelchair", label: "Wheelchair", hint: "Uses wheelchair" },
  { value: "bedridden", label: "Bedridden", hint: "Full nursing care" },
];

const MEDICAL_ELDER: TileOption[] = [
  { value: "diabetes", label: "Diabetes" },
  { value: "hypertension", label: "Hypertension" },
  { value: "heart", label: "Heart condition" },
  { value: "dementia", label: "Dementia / memory" },
  { value: "vision", label: "Vision impairment" },
  { value: "hearing", label: "Hearing impairment" },
  { value: "cancer", label: "Cancer" },
  { value: "mental_health", label: "Mental health" },
  { value: "none_known", label: "None known" },
];
const MEDICAL_ORPHAN: TileOption[] = [
  { value: "malnutrition", label: "Malnutrition" },
  { value: "chronic", label: "Chronic illness" },
  { value: "disability", label: "Disability" },
  { value: "trauma", label: "Trauma / PTSD" },
  { value: "learning", label: "Learning difficulty" },
  { value: "vaccinations", label: "Vaccinations pending" },
  { value: "none_known", label: "None known" },
];

const DIETARY: TileOption[] = [
  { value: "vegetarian", label: "Vegetarian" },
  { value: "vegan", label: "Vegan" },
  { value: "diabetic", label: "Diabetic diet" },
  { value: "soft_food", label: "Soft food only" },
  { value: "allergies", label: "Allergies" },
  { value: "none", label: "No restrictions" },
];

const DOCS: TileOption[] = [
  { value: "aadhaar", label: "Aadhaar" },
  { value: "ration", label: "Ration card" },
  { value: "birth", label: "Birth certificate" },
  { value: "pan", label: "PAN" },
  { value: "id_photo", label: "ID photo" },
  { value: "none", label: "None" },
  { value: "unsure", label: "Not sure" },
];
const DOCS_ELDER: TileOption[] = [
  ...DOCS.filter((d) => d.value !== "birth"),
  { value: "pension", label: "Pension papers" },
];

const SCHOOLING: TileOption<SchoolingStatus>[] = [
  { value: "enrolled", label: "Currently enrolled", hint: "Attending school now" },
  { value: "gap", label: "On a gap", hint: "Temporarily out of school" },
  { value: "dropped", label: "Dropped out", hint: "Left school, not enrolled" },
  { value: "never", label: "Never enrolled", hint: "Hasn't been to school" },
];

const LEGAL: TileOption<LegalStatus>[] = [
  { value: "cwc_order", label: "CWC order exists", hint: "Child has a valid CWC order" },
  { value: "in_process", label: "In process", hint: "Paperwork underway" },
  { value: "informal", label: "No paperwork yet", hint: "Need help navigating legal process" },
  { value: "unsure", label: "Unsure", hint: "Need guidance on what's required" },
];

export default function WaitlistForm({ program }: Props) {
  const isElder = program === "elder";
  const [form, setForm] = useState<FormState>(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState<
    { kind: "idle" } | { kind: "success" } | { kind: "error"; msg: string }
  >({ kind: "idle" });

  const upd = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setForm((f) => ({ ...f, [k]: v }));
  const toggle = <K extends keyof FormState>(k: K, v: string) =>
    setForm((f) => {
      const arr = f[k] as string[];
      return {
        ...f,
        [k]: arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v],
      };
    });

  const showOrg =
    form.submitterRelation === "social_worker" ||
    form.submitterRelation === "ngo" ||
    form.submitterRelation === "cwc";

  const showRelationOther = form.submitterRelation === "other";

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!form.consent) {
      setStatus({ kind: "error", msg: "Please consent before submitting." });
      return;
    }
    if (!form.applicantName || !form.submitterName || !form.phone || !form.city) {
      setStatus({ kind: "error", msg: "Please fill out the required fields." });
      return;
    }
    if (!form.submitterRelation) {
      setStatus({ kind: "error", msg: "Please tell us who is submitting." });
      return;
    }
    if (!form.urgency) {
      setStatus({ kind: "error", msg: "Please indicate how urgent this is." });
      return;
    }
    setSubmitting(true);
    setStatus({ kind: "idle" });

    const res = await postJson("/api/waitlist", { program, ...form });

    setSubmitting(false);
    if (res.ok) {
      setStatus({ kind: "success" });
      setForm(INITIAL);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStatus({ kind: "error", msg: res.error });
    }
  };

  if (status.kind === "success") {
    return (
      <div className="surface relative overflow-hidden animate-fadeUp">
        <div
          aria-hidden
          className="absolute -top-20 -right-20 h-60 w-60 rounded-full blur-3xl opacity-60"
          style={{ background: "radial-gradient(closest-side, #aee8c7, transparent)" }}
        />
        <span className="pill pill-mint">You're on the waitlist</span>
        <h3 className="text-3xl font-display mt-4 text-ink-900">
          Thank you. We've received your request.
        </h3>
        <p className="text-ink-500 mt-3 leading-relaxed">
          Our team reviews every submission personally. We'll reach out to you
          at the contact details you provided — usually within 2–5 working
          days. For immediate situations, you can also email us at{" "}
          <a
            href="mailto:hope@sanctology.com"
            className="text-royal font-medium"
          >
            hope@sanctology.com
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setStatus({ kind: "idle" })}
          className="btn-ghost mt-6"
        >
          Submit another
        </button>
      </div>
    );
  }

  const accent = isElder ? "peach" : "sky";

  return (
    <form onSubmit={onSubmit} className="surface space-y-8" noValidate>
      {/* ============ SECTION 1: About the person ============ */}
      <section className="space-y-5">
        <header>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
            Step 1 of 4
          </div>
          <h3 className="font-display text-2xl mt-1">
            {isElder ? "Who is the elder?" : "Who is the child?"}
          </h3>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="applicantName">
              {isElder ? "Elder's full name *" : "Child's full name *"}
            </label>
            <input
              id="applicantName"
              className="field-input"
              required
              value={form.applicantName}
              onChange={(e) => upd("applicantName", e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="applicantAge">
              Age
            </label>
            <input
              id="applicantAge"
              className="field-input"
              inputMode="numeric"
              value={form.applicantAge}
              onChange={(e) => upd("applicantAge", e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="field-label">Gender</div>
          <TilePicker
            accent={accent}
            value={form.applicantGender}
            onChange={(v) => upd("applicantGender", v)}
            options={GENDER}
            columns={4}
          />
        </div>

        {isElder && (
          <div>
            <div className="field-label">Mobility</div>
            <TilePicker
              accent={accent}
              value={form.applicantMobility}
              onChange={(v) => upd("applicantMobility", v)}
              options={MOBILITY}
              columns={4}
            />
          </div>
        )}

        {!isElder && (
          <>
            <div>
              <div className="field-label">Schooling status</div>
              <TilePicker
                accent={accent}
                value={form.schoolingStatus}
                onChange={(v) => upd("schoolingStatus", v)}
                options={SCHOOLING}
                columns={2}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="siblingsCount">
                Number of siblings (if any)
              </label>
              <input
                id="siblingsCount"
                className="field-input"
                inputMode="numeric"
                placeholder="0"
                value={form.siblingsCount}
                onChange={(e) => upd("siblingsCount", e.target.value)}
              />
            </div>
          </>
        )}
      </section>

      {/* ============ SECTION 2: Health + daily needs ============ */}
      <section className="space-y-5 pt-2 border-t border-ink-200/40">
        <header>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
            Step 2 of 4
          </div>
          <h3 className="font-display text-2xl mt-1">Health & daily needs</h3>
          <p className="text-sm text-ink-500 mt-1">
            Select all that apply. This helps us prepare care and a caregiver
            team before arrival.
          </p>
        </header>

        <div>
          <div className="field-label">Known medical conditions</div>
          <TileMultiPicker
            accent={accent}
            values={form.medicalConditions}
            onToggle={(v) => toggle("medicalConditions", v)}
            options={isElder ? MEDICAL_ELDER : MEDICAL_ORPHAN}
            columns={3}
          />
        </div>

        <div>
          <label className="field-label" htmlFor="medicalOther">
            Other medical / mental health notes
          </label>
          <textarea
            id="medicalOther"
            className="field-textarea"
            placeholder="Medications, diagnoses, things a carer should know."
            value={form.medicalOther}
            onChange={(e) => upd("medicalOther", e.target.value)}
          />
        </div>

        <div>
          <div className="field-label">Dietary needs</div>
          <TileMultiPicker
            accent={accent}
            values={form.dietaryNeeds}
            onToggle={(v) => toggle("dietaryNeeds", v)}
            options={DIETARY}
            columns={3}
          />
        </div>
      </section>

      {/* ============ SECTION 3: Current situation + paperwork ============ */}
      <section className="space-y-5 pt-2 border-t border-ink-200/40">
        <header>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
            Step 3 of 4
          </div>
          <h3 className="font-display text-2xl mt-1">Current situation</h3>
        </header>

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="currentLocation">
              Where are they now? *
            </label>
            <input
              id="currentLocation"
              className="field-input"
              placeholder="City / town / area"
              required
              value={form.currentLocation}
              onChange={(e) => upd("currentLocation", e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="currentLivingSituation">
              Current living situation *
            </label>
            <input
              id="currentLivingSituation"
              className="field-input"
              placeholder={
                isElder
                  ? "e.g. street, relative's home, hospital"
                  : "e.g. street, with guardian, shelter"
              }
              required
              value={form.currentLivingSituation}
              onChange={(e) => upd("currentLivingSituation", e.target.value)}
            />
          </div>
        </div>

        <div>
          <div className="field-label">Available documents</div>
          <TileMultiPicker
            accent={accent}
            values={form.documentsAvailable}
            onToggle={(v) => toggle("documentsAvailable", v)}
            options={isElder ? DOCS_ELDER : DOCS}
            columns={3}
          />
        </div>

        {!isElder && (
          <div>
            <div className="field-label">Legal / guardianship status</div>
            <TilePicker
              accent={accent}
              value={form.legalStatus}
              onChange={(v) => upd("legalStatus", v)}
              options={LEGAL}
              columns={2}
            />
            <p className="text-xs text-ink-400 mt-2">
              We work only within Indian child-welfare law. If paperwork is
              missing, we'll guide you through the CWC process — not bypass it.
            </p>
          </div>
        )}

        <div>
          <label className="field-label" htmlFor="situation">
            Tell us more *
          </label>
          <textarea
            id="situation"
            className="field-textarea min-h-[160px]"
            required
            placeholder={
              isElder
                ? "Family background, how they became homeless, what's urgent, who else is involved…"
                : "Family history, how they came into your care, what's urgent, what worries you most…"
            }
            value={form.situation}
            onChange={(e) => upd("situation", e.target.value)}
          />
        </div>

        <div>
          <div className="field-label">How urgent is this? *</div>
          <TilePicker
            accent={accent}
            value={form.urgency}
            onChange={(v) => upd("urgency", v)}
            options={URGENCY}
            columns={4}
          />
        </div>
      </section>

      {/* ============ SECTION 4: Submitter + consent ============ */}
      <section className="space-y-5 pt-2 border-t border-ink-200/40">
        <header>
          <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-ink-500">
            Step 4 of 4
          </div>
          <h3 className="font-display text-2xl mt-1">About you</h3>
        </header>

        <div>
          <div className="field-label">Who is submitting this? *</div>
          <TilePicker
            accent={accent}
            value={form.submitterRelation}
            onChange={(v) => upd("submitterRelation", v)}
            options={isElder ? RELATION_OPTIONS_ELDER : RELATION_OPTIONS_ORPHAN}
            columns={3}
          />
        </div>

        {showOrg && (
          <div className="animate-fadeUp">
            <label className="field-label" htmlFor="orgName">
              Organization / office name *
            </label>
            <input
              id="orgName"
              className="field-input"
              placeholder="Name of your NGO / social services office / CWC"
              required
              value={form.orgName}
              onChange={(e) => upd("orgName", e.target.value)}
            />
          </div>
        )}

        {showRelationOther && (
          <div className="animate-fadeUp">
            <label className="field-label" htmlFor="submitterRelationOther">
              Please describe your relation *
            </label>
            <input
              id="submitterRelationOther"
              className="field-input"
              required
              value={form.submitterRelationOther}
              onChange={(e) => upd("submitterRelationOther", e.target.value)}
            />
          </div>
        )}

        <div className="grid gap-4 sm:grid-cols-2">
          <div>
            <label className="field-label" htmlFor="submitterName">
              Your full name *
            </label>
            <input
              id="submitterName"
              className="field-input"
              required
              value={form.submitterName}
              onChange={(e) => upd("submitterName", e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="phone">
              Phone *
            </label>
            <input
              id="phone"
              type="tel"
              className="field-input"
              required
              value={form.phone}
              onChange={(e) => upd("phone", e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="email">
              Email
            </label>
            <input
              id="email"
              type="email"
              className="field-input"
              value={form.email}
              onChange={(e) => upd("email", e.target.value)}
            />
          </div>
          <div>
            <label className="field-label" htmlFor="city">
              Your city *
            </label>
            <input
              id="city"
              className="field-input"
              required
              value={form.city}
              onChange={(e) => upd("city", e.target.value)}
            />
          </div>
        </div>

        <label className="flex items-start gap-3 text-sm text-ink-700 cursor-pointer">
          <input
            type="checkbox"
            className="mt-1 h-4 w-4 rounded border-ink-300 text-royal focus:ring-royal"
            checked={form.consent}
            onChange={(e) => upd("consent", e.target.checked)}
          />
          <span>
            I consent to Sanctology Welfare Foundation contacting me about this
            waitlist submission. Information shared will be used only for
            intake and care coordination. *
          </span>
        </label>

        {status.kind === "error" && (
          <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-glow animate-fadeUp">
            {status.msg}
          </div>
        )}

        <div className="flex flex-wrap gap-3 pt-2">
          <button type="submit" className="btn-primary" disabled={submitting}>
            {submitting ? "Submitting…" : `Join the ${isElder ? "Elder" : "Orphan"} waitlist →`}
          </button>
          <span className="text-xs text-ink-500 self-center">
            We review every submission personally. Typical response: 2–5 days.
          </span>
        </div>
      </section>
    </form>
  );
}
